import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import Select from 'react-select'
import useSWR from 'swr'
import { api, createComplex, updateComplex, createVenue, updateVenue, deleteVenue, updateCourtOrder } from '../services/api'
import { Badge, Banner, Button, OverridableField, Panel } from '../components'
import { useDragReorder } from '../hooks'
import { arrayMove } from '../utils/arrayMove'
import pageStyles from '../styles/page.module.scss'
import listTableStyles from '../styles/list-table.module.scss'
import formStyles from '../styles/forms.module.scss'
import selectStyles from '../styles/react-select.module.scss'
import buttonStyles from '../components/Button.module.scss'
import styles from './ComplexDetail.module.scss'

// Placeholder ID for the new venue row
const NEW_VENUE_ID = '__new__'

interface SelectOption {
    value: string
    label: string
}

type IntlWithSupportedValues = typeof Intl & {
    supportedValuesOf?: (key: 'timeZone') => string[]
}

const TIMEZONE_OPTIONS: SelectOption[] = (
    typeof Intl !== 'undefined'
        ? (Intl as IntlWithSupportedValues).supportedValuesOf?.('timeZone') || []
        : []
).map(timezone => ({ value: timezone, label: timezone }))

interface Venue {
    tc_venue_id: string
    ext_venue_id: string | null
    tc_name: string | null
    ext_name: string | null
    tc_city: string | null
    ext_city: string | null
    tc_country_code: string | null
    ext_country_code: string | null
    country_name: string | null
    tc_map_coordinates: string | null
    ext_map_coordinates: string | null
    tc_timezone: string | null
    ext_timezone: string | null
    // Editorial position within the complex, 1 first. Null means the court is
    // placed by the derived order instead, beneath every positioned one.
    tc_court_ordinal: number | null
    created_at: string | null
    tc_updated_at: string | null
    ext_updated_at: string | null
}

interface Complex {
    tc_complex_id: string
    ext_complex_id: string | null
    tc_name: string | null
    ext_name: string | null
    sport_name: string | null
    catalog_source_name: string | null
    created_at: string | null
    tc_updated_at: string | null
    ext_updated_at: string | null
}

interface ComplexDetailsResponse {
    complex: Complex
    venues: Venue[]
    summary: {
        total_venues: number
    }
    timestamp: string
}

interface VenueEdits {
    name: string
    city: string
    country_code: string
    timezone: string
}

const getDisplayName = (tcName: string | null, extName: string | null): string => {
    return tcName || extName || 'Unnamed'
}

const ComplexDetail: React.FC = () => {
    const { complexId } = useParams<{ complexId: string }>()
    const navigate = useNavigate()
    const isCreateMode = complexId === 'new'

    // Complex name state
    const [complexName, setComplexName] = useState('')
    const [savingComplex, setSavingComplex] = useState(false)

    // Venue edits state (includes NEW_VENUE_ID for the inline new row)
    const [venueEdits, setVenueEdits] = useState<Record<string, VenueEdits>>({})
    const [savingVenues, setSavingVenues] = useState<Set<string>>(new Set())
    const [showAddVenue, setShowAddVenue] = useState(false)
    const [creatingVenue, setCreatingVenue] = useState(false)

    // Court order state. Holds venue ids in display order; the API returns the
    // venues already sorted by the same column, so this starts as a copy of
    // that and diverges only while the editor is dragging.
    const [courtOrder, setCourtOrder] = useState<string[]>([])
    const [savingCourtOrder, setSavingCourtOrder] = useState(false)

    // Error state
    const [actionError, setActionError] = useState<string | null>(null)

    // Fetch complex details (skip in create mode)
    const { data, isLoading, error, mutate } = useSWR(
        !isCreateMode && complexId ? ['complex-details', complexId] : null,
        () => api.get('/complex-details', { complex_id: complexId }),
        { revalidateOnFocus: false }
    )

    const { data: countriesData } = useSWR<{ countries: SelectOption[] }>(
        !isCreateMode ? 'countries' : null,
        () => api.get('/publishing/countries'),
        { revalidateOnFocus: false }
    )
    const countryOptions = countriesData?.countries || []

    const details: ComplexDetailsResponse | null = data?.body ? JSON.parse(data.body) : data

    // Initialize state when data loads
    useEffect(() => {
        if (details) {
            setComplexName(details.complex.tc_name || details.complex.ext_name || '')

            const initialEdits: Record<string, VenueEdits> = {}
            details.venues.forEach(venue => {
                initialEdits[venue.tc_venue_id] = {
                    name: venue.tc_name || venue.ext_name || '',
                    city: venue.tc_city || venue.ext_city || '',
                    country_code: venue.tc_country_code || venue.ext_country_code || '',
                    timezone: venue.tc_timezone || venue.ext_timezone || ''
                }
            })

            // Pre-populate new venue row from the last existing venue
            const lastVenue = details.venues[details.venues.length - 1]
            initialEdits[NEW_VENUE_ID] = {
                name: '',
                city: lastVenue ? (lastVenue.tc_city || lastVenue.ext_city || '') : '',
                country_code: lastVenue ? (lastVenue.tc_country_code || lastVenue.ext_country_code || '') : '',
                timezone: lastVenue ? (lastVenue.tc_timezone || lastVenue.ext_timezone || '') : ''
            }

            setVenueEdits(initialEdits)
            setCourtOrder(details.venues.map(venue => venue.tc_venue_id))
        }
    }, [details])

    // The order the server holds, which is what a pending drag is measured
    // against. The venues arrive already sorted by position.
    const savedCourtOrder = useMemo(
        () => details?.venues.map(venue => venue.tc_venue_id) ?? [],
        [details]
    )

    // Whether saving would change what is stored.
    //
    // Note this is NOT "did the editor drag something". Until every court has a
    // position, the list is showing a DERIVED order that nothing has committed,
    // so saving it untouched is a real change — it pins the arrangement the
    // editor is looking at. Comparing only against the order the API returned
    // made that case look like a no-op and left the button dead.
    const hasCourtOrderChanges = useMemo(() => {
        if (!details || courtOrder.length !== details.venues.length) return false
        if (details.venues.some(venue => venue.tc_court_ordinal === null)) return true
        return courtOrder.some((venueId, index) => venueId !== savedCourtOrder[index])
    }, [details, courtOrder, savedCourtOrder])

    // Whether any court currently carries an editorial position, which is what
    // there is to reset.
    const hasPositionedCourts = useMemo(
        () => details?.venues.some(venue => venue.tc_court_ordinal !== null) ?? false,
        [details]
    )

    // Render from courtOrder so a drag is visible before it is saved. Falls
    // back to the server list until the first render has populated it.
    const orderedVenues = useMemo(() => {
        if (!details) return []
        if (courtOrder.length !== details.venues.length) return details.venues
        const byId = new Map(details.venues.map(venue => [venue.tc_venue_id, venue]))
        return courtOrder
            .map(venueId => byId.get(venueId))
            .filter((venue): venue is Venue => venue !== undefined)
    }, [details, courtOrder])

    const { dragIndex, handleProps, rowProps } = useDragReorder(
        useCallback((from: number, to: number) => {
            setCourtOrder(current => arrayMove(current, from, to))
        }, []),
        { dragImageFromParent: true }
    )

    const handleSaveCourtOrder = useCallback(async () => {
        if (!complexId) return
        setSavingCourtOrder(true)
        setActionError(null)
        try {
            await updateCourtOrder(complexId, courtOrder)
            await mutate()
        } catch (err) {
            setActionError(err instanceof Error ? err.message : 'Failed to save court order')
        } finally {
            setSavingCourtOrder(false)
        }
    }, [complexId, courtOrder, mutate])

    // Clearing every position hands the whole complex back to the derived
    // order, which is the way out of a saved arrangement that went stale.
    const handleResetCourtOrder = useCallback(async () => {
        if (!complexId) return
        setSavingCourtOrder(true)
        setActionError(null)
        try {
            await updateCourtOrder(complexId, [])
            await mutate()
        } catch (err) {
            setActionError(err instanceof Error ? err.message : 'Failed to reset court order')
        } finally {
            setSavingCourtOrder(false)
        }
    }, [complexId, mutate])

    // Check if complex name has changed
    const hasComplexNameChanged = useMemo(() => {
        if (isCreateMode) return false
        if (!details) return false
        const originalName = details.complex.tc_name || details.complex.ext_name || ''
        return complexName !== originalName
    }, [isCreateMode, details, complexName])

    // Check if a venue has unsaved changes
    const hasVenueChanges = useCallback((venue: Venue) => {
        const edits = venueEdits[venue.tc_venue_id]
        if (!edits) return false

        const originalName = venue.tc_name || venue.ext_name || ''
        const originalCity = venue.tc_city || venue.ext_city || ''
        const originalCountry = venue.tc_country_code || venue.ext_country_code || ''
        const originalTimezone = venue.tc_timezone || venue.ext_timezone || ''

        return edits.name !== originalName ||
            edits.city !== originalCity ||
            edits.country_code !== originalCountry ||
            edits.timezone !== originalTimezone
    }, [venueEdits])

    // Update venue field in local state
    const handleVenueFieldChange = useCallback((venueId: string, field: keyof VenueEdits, value: string) => {
        setVenueEdits(prev => ({
            ...prev,
            [venueId]: {
                ...prev[venueId],
                [field]: value
            }
        }))
    }, [])

    // Revert venue field to external value
    const handleRevertVenueField = useCallback((venue: Venue, field: keyof VenueEdits) => {
        const extValues: Record<keyof VenueEdits, string> = {
            name: venue.ext_name || '',
            city: venue.ext_city || '',
            country_code: venue.ext_country_code || '',
            timezone: venue.ext_timezone || ''
        }
        setVenueEdits(prev => ({
            ...prev,
            [venue.tc_venue_id]: {
                ...prev[venue.tc_venue_id],
                [field]: extValues[field]
            }
        }))
    }, [])

    // Save complex name
    const handleSaveComplexName = useCallback(async () => {
        if (!complexId || isCreateMode) return

        setSavingComplex(true)
        setActionError(null)
        try {
            await updateComplex(complexId, { tc_name: complexName || undefined })
            await mutate()
        } catch (err) {
            console.error('Failed to update complex:', err)
            const message = err instanceof Error ? err.message : 'Unknown error'
            setActionError(`Failed to save complex: ${message}`)
        } finally {
            setSavingComplex(false)
        }
    }, [complexId, isCreateMode, complexName, mutate])

    // Create new complex
    const handleCreateComplex = useCallback(async () => {
        if (!complexName.trim()) {
            setActionError('Complex name is required')
            return
        }

        setSavingComplex(true)
        setActionError(null)
        try {
            const result = await createComplex({ tc_name: complexName.trim() })
            navigate(`/courts/${result.tc_complex_id}`, { replace: true })
        } catch (err) {
            console.error('Failed to create complex:', err)
            const message = err instanceof Error ? err.message : 'Unknown error'
            setActionError(`Failed to create complex: ${message}`)
            setSavingComplex(false)
        }
    }, [complexName, navigate])

    // Save a single venue
    const handleSaveVenue = useCallback(async (venue: Venue) => {
        const edits = venueEdits[venue.tc_venue_id]
        if (!edits) return

        setSavingVenues(prev => new Set(prev).add(venue.tc_venue_id))
        setActionError(null)
        try {
            await updateVenue(venue.tc_venue_id, {
                tc_name: edits.name || undefined,
                tc_city: edits.city || undefined,
                tc_country_code: edits.country_code || undefined,
                tc_timezone: edits.timezone || undefined
            })
            await mutate()
        } catch (err) {
            console.error('Failed to update venue:', err)
            const message = err instanceof Error ? err.message : 'Unknown error'
            setActionError(`Failed to save venue: ${message}`)
        } finally {
            setSavingVenues(prev => {
                const next = new Set(prev)
                next.delete(venue.tc_venue_id)
                return next
            })
        }
    }, [venueEdits, mutate])

    // Revert venue to server state
    const handleRevertVenue = useCallback((venue: Venue) => {
        setVenueEdits(prev => ({
            ...prev,
            [venue.tc_venue_id]: {
                name: venue.tc_name || venue.ext_name || '',
                city: venue.tc_city || venue.ext_city || '',
                country_code: venue.tc_country_code || venue.ext_country_code || '',
                timezone: venue.tc_timezone || venue.ext_timezone || ''
            }
        }))
    }, [])

    // Create new venue
    const handleCreateVenue = useCallback(async () => {
        if (!complexId || isCreateMode) return
        const newVenueEdits = venueEdits[NEW_VENUE_ID]
        if (!newVenueEdits?.name.trim()) {
            setActionError('Venue name is required')
            return
        }

        setCreatingVenue(true)
        setActionError(null)
        try {
            await createVenue({
                tc_complex_id: complexId,
                tc_name: newVenueEdits.name.trim(),
                tc_city: newVenueEdits.city.trim() || undefined,
                tc_country_code: newVenueEdits.country_code || undefined,
                tc_timezone: newVenueEdits.timezone || undefined
            })
            // Reset new venue row but keep city/country/timezone for next entry
            setVenueEdits(prev => ({
                ...prev,
                [NEW_VENUE_ID]: {
                    name: '',
                    city: newVenueEdits.city,
                    country_code: newVenueEdits.country_code,
                    timezone: newVenueEdits.timezone
                }
            }))
            setShowAddVenue(false)
            await mutate()
        } catch (err) {
            console.error('Failed to create venue:', err)
            const message = err instanceof Error ? err.message : 'Unknown error'
            setActionError(`Failed to create venue: ${message}`)
        } finally {
            setCreatingVenue(false)
        }
    }, [complexId, isCreateMode, venueEdits, mutate])

    // Delete venue
    const handleDeleteVenue = useCallback(async (venue: Venue) => {
        const venueName = venue.tc_name || venue.ext_name || 'this venue'
        if (!window.confirm(`Delete "${venueName}"?\n\nThis cannot be undone.`)) return

        setActionError(null)
        try {
            await deleteVenue(venue.tc_venue_id)
            await mutate()
        } catch (err) {
            console.error('Failed to delete venue:', err)
            const message = err instanceof Error ? err.message : 'Unknown error'
            setActionError(`Failed to delete venue: ${message}`)
        }
    }, [mutate])

    // Create mode view
    if (isCreateMode) {
        return (
            <div className={pageStyles.pageContainer}>
                <div className={pageStyles.pageHeader}>
                    <h1>Create Complex</h1>
                    <p>Create a new complex (venue location). You can add courts after creating the complex.</p>
                </div>

                {actionError && (
                    <Banner
                        variant="error"
                        message={actionError}
                        onDismiss={() => setActionError(null)}
                        autoScroll
                    />
                )}

                <div className={formStyles.formContainer}>
                    <div className={formStyles.formSection}>
                        <h3>Complex Name</h3>
                        <div className={formStyles.formField}>
                            <input
                                type="text"
                                value={complexName}
                                onChange={(e) => setComplexName(e.target.value)}
                                placeholder="Enter complex name (e.g., Melbourne Park)"
                                autoFocus
                            />
                        </div>
                    </div>

                    <div className={formStyles.formFooter}>
                        <Link
                            to="/courts"
                            className={`${buttonStyles.button} ${buttonStyles.secondary} ${buttonStyles.medium}`}
                        >
                            Cancel
                        </Link>
                        <Button
                            onClick={handleCreateComplex}
                            disabled={!complexName.trim() || savingComplex}
                        >
                            {savingComplex ? 'Creating...' : 'Create Complex'}
                        </Button>
                    </div>
                </div>
            </div>
        )
    }

    // Error state
    if (error) {
        return (
            <div className={pageStyles.pageContainer}>
                <div className={pageStyles.error}>
                    <h2>Error loading complex</h2>
                    <p>Failed to fetch complex details: {(error as Error).message}</p>
                    <Button variant="danger" onClick={() => mutate()}>Retry</Button>
                </div>
            </div>
        )
    }

    // Loading state
    if (isLoading || !details) {
        return (
            <div className={pageStyles.pageContainer}>
                <div className={pageStyles.loading}>
                    <h3>Loading complex details...</h3>
                    <p>Please wait while we fetch the complex data.</p>
                </div>
            </div>
        )
    }

    const complex = details.complex

    return (
        <>
            {/* Header Button */}
            <div className={pageStyles.headerButtons}>
                <Button onClick={() => setShowAddVenue(true)} disabled={showAddVenue}>
                    + Add Court
                </Button>
            </div>

            {/* Header */}
            <div className={pageStyles.pageHeader}>
                <h1>{getDisplayName(complex.tc_name, complex.ext_name)}</h1>
                <p>
                    {complex.ext_complex_id && (
                        <code className="id-code">{complex.ext_complex_id}</code>
                    )}
                    {' '}
                    {complex.sport_name && <Badge variant="primary">{complex.sport_name}</Badge>}
                    {' '}
                    {complex.catalog_source_name && <Badge variant="secondary">{complex.catalog_source_name}</Badge>}
                    {!complex.ext_complex_id && <Badge variant="neutral">Manually Created</Badge>}
                    {' · '}
                    <span className={styles.venueCount}>
                        {details.venues.length} court{details.venues.length !== 1 ? 's' : ''}
                    </span>
                </p>
            </div>

            {/* Error Banner */}
            {actionError && (
                <Banner
                    variant="error"
                    message={actionError}
                    onDismiss={() => setActionError(null)}
                    autoScroll
                />
            )}

            {/* Complex Name Section */}
            <Panel>
                <Panel.Header
                    trailing={hasComplexNameChanged && (
                        <div className={styles.headerActions}>
                            <Button
                                onClick={handleSaveComplexName}
                                size="small"
                                disabled={savingComplex}
                            >
                                {savingComplex ? 'Saving...' : 'Save'}
                            </Button>
                            <Button
                                variant="secondary"
                                onClick={() => setComplexName(complex.tc_name || complex.ext_name || '')}
                                size="small"
                            >
                                Revert
                            </Button>
                        </div>
                    )}
                >
                    <h3>Complex Details</h3>
                </Panel.Header>
                <OverridableField
                    label="Name"
                    tcValue={complexName}
                    extValue={complex.ext_name}
                    onRevert={() => setComplexName(complex.ext_name || '')}
                    modifiedClassName="modified"
                >
                    <input
                        type="text"
                        value={complexName}
                        onChange={(e) => setComplexName(e.target.value)}
                        disabled={savingComplex}
                        placeholder="Complex name"
                    />
                </OverridableField>
            </Panel>

            {/* Venues List */}
            <div className={styles.section}>
                <div className={styles.courtOrderBar}>
                    <p className={styles.courtOrderHint}>
                        Drag a court to set the order it appears in on the Live page. Saving
                        pins every court in this list; reset to let the schedule order them.
                    </p>
                    <div className={styles.courtOrderActions}>
                        {hasPositionedCourts && (
                            <Button
                                variant="secondary"
                                size="small"
                                onClick={handleResetCourtOrder}
                                disabled={savingCourtOrder}
                            >
                                Reset to automatic
                            </Button>
                        )}
                        <Button
                            size="small"
                            onClick={handleSaveCourtOrder}
                            disabled={savingCourtOrder || !hasCourtOrderChanges}
                        >
                            {savingCourtOrder ? 'Saving…' : 'Save order'}
                        </Button>
                    </div>
                </div>

                <div className={`${listTableStyles.tableHeader} ${styles.venuesGrid}`}>
                    <div></div>
                    <div>Court Name</div>
                    <div>City</div>
                    <div>Country</div>
                    <div>Timezone</div>
                    <div></div>
                </div>

                <div className={listTableStyles.tableBody}>
                    {/* Existing venues, in the order the Live page draws them */}
                    {orderedVenues.map((venue, venueIndex) => {
                        const edits = venueEdits[venue.tc_venue_id]
                        const isSaving = savingVenues.has(venue.tc_venue_id)
                        const hasChanges = hasVenueChanges(venue)
                        const canDelete = !venue.ext_venue_id
                        const selectedCountryOption = edits?.country_code
                            ? countryOptions.find(option => option.value === edits.country_code) || {
                                value: edits.country_code,
                                label: venue.country_name || edits.country_code
                            }
                            : null
                        const selectedTimezoneOption = edits?.timezone
                            ? TIMEZONE_OPTIONS.find(option => option.value === edits.timezone) || {
                                value: edits.timezone,
                                label: edits.timezone
                            }
                            : null

                        return (
                            <div
                                key={venue.tc_venue_id}
                                className={`${listTableStyles.tableRow} ${styles.venuesGrid} ${dragIndex === venueIndex ? styles.dragging : ''}`}
                                {...rowProps(venueIndex)}
                            >
                                <div
                                    className={styles.dragHandle}
                                    title="Drag to reorder"
                                    aria-label="Drag to reorder"
                                    {...handleProps(venueIndex)}
                                >
                                    ⠿
                                </div>

                                <div>
                                    {edits ? (
                                        <OverridableField
                                            compact
                                            tcValue={edits.name}
                                            extValue={venue.ext_name}
                                            onRevert={() => handleRevertVenueField(venue, 'name')}
                                            modifiedClassName="modified"
                                        >
                                            <input
                                                type="text"
                                                value={edits.name}
                                                onChange={(e) => handleVenueFieldChange(venue.tc_venue_id, 'name', e.target.value)}
                                                disabled={isSaving}
                                                placeholder="Court name"
                                            />
                                        </OverridableField>
                                    ) : (
                                        <span>{getDisplayName(venue.tc_name, venue.ext_name)}</span>
                                    )}
                                </div>

                                <div>
                                    {edits ? (
                                        <OverridableField
                                            compact
                                            tcValue={edits.city}
                                            extValue={venue.ext_city}
                                            onRevert={() => handleRevertVenueField(venue, 'city')}
                                            modifiedClassName="modified"
                                        >
                                            <input
                                                type="text"
                                                value={edits.city}
                                                onChange={(e) => handleVenueFieldChange(venue.tc_venue_id, 'city', e.target.value)}
                                                disabled={isSaving}
                                                placeholder="City"
                                            />
                                        </OverridableField>
                                    ) : (
                                        <span>{venue.tc_city || venue.ext_city || '—'}</span>
                                    )}
                                </div>

                                <div>
                                    {edits ? (
                                        <OverridableField
                                            compact
                                            tcValue={edits.country_code}
                                            extValue={venue.ext_country_code}
                                            onRevert={() => handleRevertVenueField(venue, 'country_code')}
                                            modifiedClassName={selectStyles.modified}
                                        >
                                            <Select
                                                options={countryOptions}
                                                value={selectedCountryOption}
                                                onChange={option => handleVenueFieldChange(venue.tc_venue_id, 'country_code', option?.value || '')}
                                                isDisabled={isSaving}
                                                aria-label="Country"
                                                placeholder="Select country..."
                                                className={selectStyles.reactSelect}
                                                classNamePrefix="react-select"
                                            />
                                        </OverridableField>
                                    ) : (
                                        <span>{venue.country_name || venue.tc_country_code || venue.ext_country_code || '—'}</span>
                                    )}
                                </div>

                                <div>
                                    {edits ? (
                                        <OverridableField
                                            compact
                                            tcValue={edits.timezone}
                                            extValue={venue.ext_timezone}
                                            onRevert={() => handleRevertVenueField(venue, 'timezone')}
                                            modifiedClassName={selectStyles.modified}
                                        >
                                            <Select
                                                options={TIMEZONE_OPTIONS}
                                                value={selectedTimezoneOption}
                                                onChange={option => handleVenueFieldChange(venue.tc_venue_id, 'timezone', option?.value || '')}
                                                isDisabled={isSaving}
                                                aria-label="Timezone"
                                                placeholder="Select timezone..."
                                                className={selectStyles.reactSelect}
                                                classNamePrefix="react-select"
                                            />
                                        </OverridableField>
                                    ) : (
                                        <span>{venue.tc_timezone || venue.ext_timezone || '—'}</span>
                                    )}
                                </div>

                                <div className={styles.venueActions}>
                                    {hasChanges && (
                                        <>
                                            <Button
                                                size="small"
                                                onClick={() => handleSaveVenue(venue)}
                                                disabled={isSaving}
                                            >
                                                {isSaving ? '...' : 'Save'}
                                            </Button>
                                            <Button
                                                variant="secondary"
                                                size="small"
                                                onClick={() => handleRevertVenue(venue)}
                                            >
                                                ↻
                                            </Button>
                                        </>
                                    )}
                                    {canDelete && !hasChanges && (
                                        <Button
                                            variant="secondary"
                                            size="small"
                                            onClick={() => handleDeleteVenue(venue)}
                                            title="Delete court"
                                        >
                                            ✕
                                        </Button>
                                    )}
                                </div>
                            </div>
                        )
                    })}

                    {/* Inline "Add New Court" row */}
                    {showAddVenue && venueEdits[NEW_VENUE_ID] && (
                        <div className={`${listTableStyles.tableRow} ${styles.venuesGrid} ${styles.newVenueRow}`}>
                            {/* Empty cell under the drag handles: a court that
                                does not exist yet has no position to drag. */}
                            <div></div>

                            <div>
                                <input
                                    type="text"
                                    value={venueEdits[NEW_VENUE_ID].name}
                                    onChange={(e) => handleVenueFieldChange(NEW_VENUE_ID, 'name', e.target.value)}
                                    disabled={creatingVenue}
                                    placeholder="New court name..."
                                    autoFocus
                                />
                            </div>
                            <div>
                                <input
                                    type="text"
                                    value={venueEdits[NEW_VENUE_ID].city}
                                    onChange={(e) => handleVenueFieldChange(NEW_VENUE_ID, 'city', e.target.value)}
                                    disabled={creatingVenue}
                                    placeholder="City"
                                />
                            </div>
                            <div>
                                <Select
                                    options={countryOptions}
                                    value={countryOptions.find(option => option.value === venueEdits[NEW_VENUE_ID].country_code) || (venueEdits[NEW_VENUE_ID].country_code ? { value: venueEdits[NEW_VENUE_ID].country_code, label: venueEdits[NEW_VENUE_ID].country_code } : null)}
                                    onChange={option => handleVenueFieldChange(NEW_VENUE_ID, 'country_code', option?.value || '')}
                                    isClearable
                                    isDisabled={creatingVenue}
                                    aria-label="Country"
                                    placeholder="Select country..."
                                    className={selectStyles.reactSelect}
                                    classNamePrefix="react-select"
                                />
                            </div>
                            <div>
                                <Select
                                    options={TIMEZONE_OPTIONS}
                                    value={TIMEZONE_OPTIONS.find(option => option.value === venueEdits[NEW_VENUE_ID].timezone) || (venueEdits[NEW_VENUE_ID].timezone ? { value: venueEdits[NEW_VENUE_ID].timezone, label: venueEdits[NEW_VENUE_ID].timezone } : null)}
                                    onChange={option => handleVenueFieldChange(NEW_VENUE_ID, 'timezone', option?.value || '')}
                                    isClearable
                                    isDisabled={creatingVenue}
                                    aria-label="Timezone"
                                    placeholder="Select timezone..."
                                    className={selectStyles.reactSelect}
                                    classNamePrefix="react-select"
                                />
                            </div>
                            <div className={styles.venueActions}>
                                <Button
                                    size="small"
                                    onClick={handleCreateVenue}
                                    disabled={!venueEdits[NEW_VENUE_ID].name.trim() || creatingVenue}
                                >
                                    {creatingVenue ? '...' : 'Add'}
                                </Button>
                                <Button
                                    variant="secondary"
                                    size="small"
                                    onClick={() => setShowAddVenue(false)}
                                    disabled={creatingVenue}
                                >
                                    ✕
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    )
}

export default ComplexDetail
